import axios from 'axios'
import applyCaseMiddleware from 'axios-case-converter'
import Cookies from 'universal-cookie'
import localStore from '@service/local-store'

/**
 * @author hmahesh3_uhg
 *
 * Axios instance pointing to base api url
 * The instance has a middleware to convert snake_case to camelCase for responses
 * and camelCase to snake_case for requests
 */

export const getApiOrigin = (): string => {
  const envValue = process.env.REACT_APP_API_ORIGIN || ''

  if (!envValue) return ''

  try {
    // Parse as JSON object mapping domain origins to API origins
    const apiOriginMap = JSON.parse(envValue)
    const currentOrigin = window.location.origin
    return apiOriginMap[currentOrigin] || ''
  } catch {
    // Backward compatibility: treat as simple string if not valid JSON
    return envValue
  }
}

const API_ORIGIN = getApiOrigin()
const API_BASE = `${API_ORIGIN}/api/v1`
const UUID_KEY_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * Factory function to create axios instances with correct baseURL
 * @param apiPath - Optional path suffix (e.g., '/auth/', '/sysadmin')
 * @param options - Configuration options
 * @param options.useCaseMiddleware - Whether to apply snake_case/camelCase conversion (default: false)
 * @param options.axiosConfig - Additional axios configuration to merge
 */
export const createApiClient = (
  apiPath = '',
  options: {
    useCaseMiddleware?: boolean
    axiosConfig?: Parameters<typeof axios.create>[0]
  } = {}
) => {
  const { useCaseMiddleware = false, axiosConfig = {} } = options

  const baseURL = apiPath ? `${API_BASE}${apiPath}` : API_BASE

  const instance = axios.create({
    baseURL,
    withCredentials: true,
    ...axiosConfig,
  })

  return useCaseMiddleware
    ? applyCaseMiddleware(instance, {
        // Keep UUID map keys intact (survey IDs, etc.) during case conversion.
        preservedKeys: (input: string) => UUID_KEY_PATTERN.test(input),
      })
    : instance
}

const client = createApiClient('', { useCaseMiddleware: true })

const cookies = new Cookies()

client.interceptors.request.use((config) => {
  const url = config.url || ''

  if (url.startsWith('/providers')) {
    const authType = cookies.get('auth_type') || localStore.get('auth_type')

    if (authType) {
      const existingParams = (config.params || {}) as Record<string, unknown>

      if (!('auth_type' in existingParams)) {
        config.params = {
          ...existingParams,
          auth_type: authType,
        }
      }
    }
  }

  return config
})

// @ts-ignore
window.client = client

export default client

export const getAvatarUrl = (imagePath: string): string | undefined => {
  if (!imagePath) return undefined
  return `${API_BASE}/misc/image?image_path=${encodeURIComponent(imagePath)}`
}
